import React from 'react';
import SafeHtml from '../components/common/SafeHtml';
import { getShortSummaryDisplayContent } from './shortSummaryUtils';

const truncateDocumentSummary = (summary) => {
  if (!summary) return '';
  
  // First, try to get the first paragraph
  const paragraphs = summary.split('</p>');
  if (paragraphs.length > 1) {
    return paragraphs[0] + '</p>';
  }
  
  // If no paragraph tags, try to get first 4 sentences
  const sentences = summary.split(/[.!?]+/);
  if (sentences.length > 4) {
    const truncated = sentences.slice(0, 4).join('. ').trim();
    return truncated + (truncated.endsWith('.') ? '' : '.');
  }
  
  return summary;
};

// Extract display state string from docDisplayStates entry (backward compat)
export const getDocDisplayState = (entry) => {
  if (!entry) return 'normal';
  const state = typeof entry === 'string' ? entry : (entry.state || 'normal');
  // Convert legacy 'shrink' state to 'normal'
  return state === 'shrink' ? 'normal' : state;
};

// Extract selected field from docDisplayStates entry (single field string or legacy array)
export const getDocSelectedField = (entry) => {
  if (!entry || typeof entry === 'string') return null;
  // Support legacy array format — use last selected
  if (Array.isArray(entry.fields)) return entry.fields[entry.fields.length - 1] || null;
  return entry.field || null;
};

// Resolve a field's summary content from the document
const getFieldContent = (doc, fieldName) => {
  if (fieldName === 'short_summary') {
    return getShortSummaryDisplayContent(doc);
  }

  const field = doc[fieldName];
  if (!field) return null;
  if (typeof field === 'string') return field;
  if (field.summary) return field.summary;
  return null;
};

// Check whether a document has any expandable content (short_summary or primaryField data)
export const hasExpandableContent = (doc, primaryFields = []) => {
  return primaryFields.some(f => getFieldContent(doc, f.fieldName));
};

export const showDocumentDetails = (doc, primaryFields = [], savedSelectedField = null, onSelectField = null) => {
  // Filter primaryFields to only those with data in this document, short_summary first, rest alpha by displayName
  const availableChips = primaryFields
    .filter(f => getFieldContent(doc, f.fieldName))
    .sort((a, b) => {
      if (a.fieldName === 'short_summary') return -1;
      if (b.fieldName === 'short_summary') return 1;
      return a.displayName.localeCompare(b.displayName);
    });

  if (availableChips.length === 0) return null;

  // Determine the active field (single select)
  let activeField;
  if (savedSelectedField && availableChips.some(c => c.fieldName === savedSelectedField)) {
    activeField = savedSelectedField;
  } else {
    // Default: short_summary if available, otherwise first chip
    activeField = availableChips.some(c => c.fieldName === 'short_summary')
      ? 'short_summary'
      : availableChips[0]?.fieldName;
  }

  const activeIndex = availableChips.findIndex(f => f.fieldName === activeField);

  const navigateChip = (direction, e) => {
    e.stopPropagation();
    const newIndex = direction === 'prev'
      ? (activeIndex - 1 + availableChips.length) % availableChips.length
      : (activeIndex + 1) % availableChips.length;
    onSelectField?.(availableChips[newIndex].fieldName);
  };

  const content = getFieldContent(doc, activeField);
  const chipInfo = availableChips.find(f => f.fieldName === activeField);
  const label = chipInfo?.displayName || activeField;
  const rawContent = activeField === 'document_summary' ? truncateDocumentSummary(content) : content;
  // Normalize newlines for HTML render: some fields arrive with literal "\n" escape sequences
  // (from a JSON round-trip upstream) and others with real newlines. Both need <br> for the
  // SafeHtml render. See docs/updates/2026-04-20-library-transcript-newline-chars.md
  const displayContent = typeof rawContent === 'string'
    ? rawContent.replace(/\\n/g, '<br>').replace(/\r?\n/g, '<br>')
    : rawContent;

  return (
    <div className="document-hover-details">
      {availableChips.length > 1 && (
        <div className="expanded-field-chips">
          <button
            className="chip-nav-arrow"
            onClick={(e) => navigateChip('prev', e)}
            aria-label="Previous field"
          >
            ‹
          </button>
          {availableChips.map(field => (
            <button
              key={field.fieldName}
              className={`expanded-field-chip ${field.fieldName === activeField ? 'chip-selected' : ''}`}
              onClick={(e) => { e.stopPropagation(); onSelectField?.(field.fieldName); }}
            >
              {field.displayName}
            </button>
          ))}
          <button
            className="chip-nav-arrow"
            onClick={(e) => navigateChip('next', e)}
            aria-label="Next field"
          >
            ›
          </button>
        </div>
      )}
      {content && (
        <div className="expanded-detail-section">
          <SafeHtml as="p" html={displayContent} />
        </div>
      )}
    </div>
  );
};

export const calculateDuration = (timeRange) => {
  if (!timeRange || typeof timeRange !== 'string') return '';
  
  const parts = timeRange.split(' - ');
  if (parts.length !== 2) return '';
  
  const [startStr, endStr] = parts;
  
  const parseTime = (timeStr) => {
    const [time, period] = timeStr.trim().split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    let totalMinutes = hours * 60 + minutes;
    if (period === 'PM' && hours !== 12) totalMinutes += 12 * 60;
    if (period === 'AM' && hours === 12) totalMinutes -= 12 * 60;
    return totalMinutes;
  };
  
  try {
    const startMinutes = parseTime(startStr);
    const endMinutes = parseTime(endStr);
    const duration = endMinutes - startMinutes;
    return duration > 6 ? `${duration} min` : '';
  } catch (error) {
    return '';
  }
};
